/**
 * MQTT Logout Service
 * Handles cross-app logout using device ID
 * When user logs out from one app, publishes logout event to other apps via MQTT
 */

import mqtt from 'mqtt';
import { getOrCreateDeviceId } from './deviceIdService';

// MQTT Client for logout notifications
let logoutClient = null;
let messageHandlers = [];
let isInitializing = false;
let initializationPromise = null;

/**
 * Initialize MQTT client for logout notifications
 * Returns a promise that resolves when connected
 */
export const initMqttLogoutClient = () => {
  // If already connected, return immediately
  if (logoutClient && logoutClient.connected) {
    console.log('[MQTT Logout Service] Client already connected');
    return Promise.resolve(logoutClient);
  }

  // If currently initializing, return existing promise
  if (isInitializing && initializationPromise) {
    console.log('[MQTT Logout Service] Initialization in progress, returning existing promise');
    return initializationPromise;
  }

  // Start new initialization
  isInitializing = true;
  initializationPromise = new Promise((resolve, reject) => {
    try {
      console.log('[MQTT Logout Service] Initializing MQTT client...');
      logoutClient = mqtt.connect('wss://mqtt.kit19.com:8084/mqtt', {
        clientId: `logout_${Date.now()}_${Math.random().toString(16).substring(2, 8)}`,
        username: 'netadmin',
        password: 'admin',
        keepalive: 30,
        reconnect: true,
        reconnectPeriod: 1000,
        connectTimeout: 15000,
        clean: false,
        rejectUnauthorized: false
      });

      logoutClient.on('connect', () => {
        console.log('[MQTT Logout Service] ✅ Connected to broker');
        isInitializing = false;
        subscribeToLogoutChannel();
        resolve(logoutClient);
      });

      logoutClient.on('reconnect', () => {
        console.log('[MQTT Logout Service] ⚙️ Reconnecting to broker...');
      });

      logoutClient.on('disconnect', () => {
        console.log('[MQTT Logout Service] Disconnected from broker');
      });

      logoutClient.on('error', (error) => {
        console.error('[MQTT Logout Service] Error:', error.message);
        if (isInitializing) {
          isInitializing = false;
          reject(error);
        }
      });

      logoutClient.on('message', (topic, message) => {
        try {
          const data = JSON.parse(message.toString());
          console.log('[MQTT Logout Service] 📨 Message received on topic:', topic, data);
          
          if (data.type === 'LOGOUT_EVENT') {
            console.log('[MQTT Logout Service] Processing LOGOUT_EVENT with', messageHandlers.length, 'handlers');
            // Call all registered handlers
            messageHandlers.forEach(handler => {
              try {
                handler(data);
              } catch (e) {
                console.error('[MQTT Logout Service] Handler error:', e);
              }
            });
          }
        } catch (error) {
          console.error('[MQTT Logout Service] Message parse error:', error);
        }
      });

      // Set a timeout for connection
      const connectTimeout = setTimeout(() => {
        if (isInitializing) {
          isInitializing = false;
          reject(new Error('MQTT connection timeout'));
        }
      }, 15000);

      // If connection succeeds before timeout, clear it
      const originalResolve = resolve;
      resolve = (client) => {
        clearTimeout(connectTimeout);
        originalResolve(client);
      };

    } catch (error) {
      console.error('[MQTT Logout Service] Failed to initialize:', error);
      isInitializing = false;
      reject(error);
    }
  });

  return initializationPromise;
};

/**
 * Get device ID (generate if not exists)
 * Uses centralized device ID service to ensure all apps share the same ID
 */
export const getDeviceId = () => {
  return getOrCreateDeviceId();
};

/**
 * Publish logout event to MQTT
 * Other apps listening will receive this and logout
 */
export const publishLogoutEvent = async (deviceId, userId, appName) => {
  try {
    console.log('[MQTT Logout Service] Publishing logout event for device:', deviceId);
    
    // Ensure client is connected
    let client = logoutClient;
    if (!client || !client.connected) {
      console.log('[MQTT Logout Service] Client not connected, initializing...');
      client = await initMqttLogoutClient();
    }
    
    if (!client || !client.connected) {
      console.error('[MQTT Logout Service] Unable to connect to MQTT broker');
      throw new Error('MQTT broker connection failed');
    }

    return performPublish(deviceId, userId, appName);
  } catch (error) {
    console.error('[MQTT Logout Service] Publish error:', error);
    throw error;
  }
};

/**
 * Perform the actual MQTT publish
 */
const performPublish = (deviceId, userId, appName) => {
  return new Promise((resolve, reject) => {
    try {
      const logoutTopic = `kit19/logout/${deviceId}`;
      const logoutPayload = {
        type: 'LOGOUT_EVENT',
        deviceId,
        userId,
        appName,
        timestamp: new Date().toISOString(),
        action: 'logout_from_device'
      };

      console.log('[MQTT Logout Service] Publishing to topic:', logoutTopic);
      logoutClient.publish(logoutTopic, JSON.stringify(logoutPayload), { qos: 1 }, (error) => {
        if (error) {
          console.error('[MQTT Logout Service] Publish failed:', error);
          reject(error);
        } else {
          console.log('[MQTT Logout Service] ✅ Logout event published to:', logoutTopic);
          resolve(true);
        }
      });
    } catch (error) {
      console.error('[MQTT Logout Service] Publish error:', error);
      reject(error);
    }
  });
};

/**
 * Subscribe to logout channel for this device
 */
const subscribeToLogoutChannel = () => {
  try {
    const deviceId = getDeviceId();
    const logoutTopic = `kit19/logout/${deviceId}`;
    
    logoutClient.subscribe(logoutTopic, (error) => {
      if (!error) {
        console.log('[MQTT Logout Service] Subscribed to logout channel:', logoutTopic);
      } else {
        console.error('[MQTT Logout Service] Subscribe error:', error);
      }
    });
  } catch (error) {
    console.error('[MQTT Logout Service] Subscribe error:', error);
  }
};

/**
 * Listen for logout events on this device
 * Triggers callback when another app logs out
 */
export const onLogoutEvent = (callback) => {
  try {
    const client = initMqttLogoutClient();
    
    if (!client) {
      console.error('[MQTT Logout Service] Unable to initialize client');
      return null;
    }

    // Create a handler for this specific callback
    const handler = (data) => {
      if (callback && typeof callback === 'function') {
        callback({
          deviceId: data.deviceId,
          userId: data.userId,
          appName: data.appName,
          timestamp: data.timestamp
        });
      }
    };

    // Register this handler
    messageHandlers.push(handler);
    console.log('[MQTT Logout Service] Handler registered. Total handlers:', messageHandlers.length);

    // Return unsubscribe function
    return () => {
      const index = messageHandlers.indexOf(handler);
      if (index > -1) {
        messageHandlers.splice(index, 1);
        console.log('[MQTT Logout Service] Handler unregistered. Remaining handlers:', messageHandlers.length);
      }
    };
  } catch (error) {
    console.error('[MQTT Logout Service] Setup listener error:', error);
    return null;
  }
};

/**
 * Disconnect logout client
 */
export const disconnectLogoutClient = () => {
  if (logoutClient) {
    logoutClient.end(() => {
      console.log('[MQTT Logout Service] Disconnected');
    });
    logoutClient = null;
  }
};

/**
 * Get logout topic for device
 */
export const getLogoutTopic = (deviceId) => {
  return `kit19/logout/${deviceId}`;
};

/**
 * Broadcast logout to all instances of user across devices
 * Publishes to a global logout channel for the user
 */
export const broadcastLogoutAcrossDevices = async (userId, deviceId) => {
  try {
    // Ensure client is connected
    let client = logoutClient;
    if (!client || !client.connected) {
      console.log('[MQTT Logout Service] Client not connected, initializing...');
      client = await initMqttLogoutClient();
    }
    
    if (!client || !client.connected) {
      console.error('[MQTT Logout Service] Unable to connect to MQTT broker');
      throw new Error('MQTT broker connection failed');
    }

    return performBroadcast(userId, deviceId);
  } catch (error) {
    console.error('[MQTT Logout Service] Broadcast error:', error);
    throw error;
  }
};

/**
 * Perform the actual broadcast
 */
const performBroadcast = (userId, deviceId) => {
  return new Promise((resolve, reject) => {
    try {
      const globalLogoutTopic = `kit19/user_logout/${userId}`;
      const broadcastPayload = {
        type: 'GLOBAL_LOGOUT',
        userId,
        deviceId,
        timestamp: new Date().toISOString(),
        action: 'logout_all_devices'
      };

      logoutClient.publish(globalLogoutTopic, JSON.stringify(broadcastPayload), { qos: 1 }, (error) => {
        if (error) {
          console.error('[MQTT Logout Service] Broadcast failed:', error);
          reject(error);
        } else {
          console.log('[MQTT Logout Service] Global logout broadcast sent for user:', userId);
          resolve(true);
        }
      });
    } catch (error) {
      console.error('[MQTT Logout Service] Broadcast error:', error);
      reject(error);
    }
  });
};

const mqttLogoutServiceExport = {
  initMqttLogoutClient,
  getDeviceId,
  publishLogoutEvent,
  onLogoutEvent,
  disconnectLogoutClient,
  getLogoutTopic,
  broadcastLogoutAcrossDevices
};

export default mqttLogoutServiceExport;
